import { Button, Card, Flex, Table, Text } from "@radix-ui/themes";
import React, { useEffect, useRef, useState } from 'react';
import { useWatch } from "react-hook-form";
import toast from "react-hot-toast";
import { useMutation } from "react-query";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../hooks/hooks";
import { createExportedDocument, createStudentSubjectGrade } from "../../services/school/student/createStudent";
import { AcademicYearType } from "../../types/AcademicYear";
import { AssessmentType, ExamType, StudentAssessmentType } from "../../types/Assessment";
import { ClassType } from "../../types/Class";
import { StudentClassType } from '../../types/Student';
import { StudentSubjectGradeType } from "../../types/StudentSubjectGrade";
import { SubjectType } from "../../types/Subject";
import { RegimeOptions } from "../../utils/studentUtils";
import { omit, stringToBoolean } from "../../utils/utilities";
import { DocumentTemplateType, StudentGrade } from "./StudentGradesForm";
import { useLogoBase64 } from "../../hooks/useLogoBase64";
import { useExportDocument } from "../../hooks/useExportDocument";
import { useIES } from "../../hooks/useIES";

interface StudentGradesFormProps {
  students: Array<StudentClassType>;
  assessments: Array<AssessmentType>;
  control: any;
  errors: any;
  readonly?: boolean;
  classe?: ClassType;
  subject?: SubjectType;
  currentAcademicYear?: AcademicYearType,
  canPublishResults?: boolean;
}

export const StudentExamGradesForm: React.FC<StudentGradesFormProps> = ({ students, assessments, control, errors, readonly, classe, subject, currentAcademicYear, canPublishResults }) => {
  const userData = useAppSelector((state) => state.login.userData);
  const {logoBase64} = useLogoBase64();
    const { IES } = useIES();
  
  const { exportDocument } = useExportDocument();
  const watchAllAssessments = useWatch({
    control,
    name: 'studentAssessments',
  });
  const mutationCreateStudentSubjectGrade = useMutation({ mutationFn: createStudentSubjectGrade, mutationKey: "mutationCreateStudentSubjectGrade" })

  const [tableData, setTableData] = useState<{ headers: string[]; rows: string[][] }>({
    headers: [],
    rows: []
  });

  const tableRef = useRef<HTMLTableElement>(null);

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const determineResult = (
    frequencyGrade: number,
    studentAssessments: StudentAssessmentType[]
  ): string => {

    const examNormal = studentAssessments.find(sa =>
      assessments.some(a => a.type === 'Exame Normal' && a.id === sa.idAssessment)
    );
    let { grade: examNormalGrade, weight: examNormalWeight } = examNormal ?? { grade: 0, weight: 0 };

    if (examNormalWeight === 0) {
      examNormalWeight = 50;
    }

    const examRecorrente = studentAssessments.find(sa =>
      assessments.some(a => a.type === 'Exame de Recorrência' && a.id === sa.idAssessment)
    );
    let { grade: examRecorrenteGrade, weight: examRecorrenteWeight } = examRecorrente ?? { grade: 0, weight: 0 };

    if (examRecorrenteWeight === 0) {
      examRecorrenteWeight = 50;
    }

    if (examNormal !== undefined) {
      const mediaNormal = ((frequencyGrade * (100 - (examNormalWeight ?? 0))) + (examNormalGrade * (examNormalWeight ?? 0))) / 100;
      if (examNormalGrade >= IES.MINIMUM_PASSING_GRADE && mediaNormal >= IES.MINIMUM_PASSING_GRADE) {
        return "Aprovado";
      }

      if (examRecorrente === undefined) {
        return "Recorrência";
      }
    }

    if (examRecorrente !== undefined) {
      const mediaRecorrente = ((frequencyGrade * (100 - (examRecorrenteWeight ?? 0))) + (examRecorrenteGrade * (examRecorrenteWeight ?? 0))) / 100;
      if (examRecorrenteGrade >= IES.MINIMUM_PASSING_GRADE && mediaRecorrente >= IES.MINIMUM_PASSING_GRADE) {
        return "Aprovado";
      } else {
        return "Reprovado";
      }
    }

    return "Reprovado";
  };

  const getFrequencyGrade = (studentAssessments: StudentAssessmentType[]) => {
    const nonExamAssessments = assessments.filter(
      a => !['Exame Normal', 'Exame de Recorrência', 'Exame Especial'].includes(a.type as ExamType)
    );

    const output = nonExamAssessments.reduce((total, assessment) => {
      const sa = studentAssessments.find(sa => sa.idAssessment === assessment.id);
      const grade = sa?.grade ?? 0;
      const weight = assessment.weight ?? 0;
      return total + (grade * weight) / 100;
    }, 0);

    return Math.round(output);
  };

  const calculateFinalGradeV2 = (studentAssessments: StudentAssessmentType[]) => {
    const frequencyGrade = getFrequencyGrade(studentAssessments);

    const { grade: examNormal, weight: examNormalWeight } = studentAssessments.find(sa =>
      assessments.some(a => a.type === 'Exame Normal' && a.id === sa.idAssessment)
    ) ?? { grade: 0, weight: 0 };

    const { grade: examRecorrente, weight: examRecorrenteWeight } = studentAssessments.find(sa =>
      assessments.some(a => a.type === 'Exame de Recorrência' && a.id === sa.idAssessment)
    ) ?? { grade: 0, weight: 0 };

    //const exameUtilizado = Math.max(examNormal, examRecorrente);

    const isRecorrenteUsed = examRecorrente > examNormal;
    const exameUtilizado = isRecorrenteUsed ? examRecorrente : examNormal;
    let exameUtilizadoWeight = isRecorrenteUsed ? examRecorrenteWeight : examNormalWeight;

    if (exameUtilizado === 0) return 0;

    if (exameUtilizadoWeight === 0) exameUtilizadoWeight = 50;

    const finalGrade = (((frequencyGrade * (100 - (exameUtilizadoWeight ?? 0))) + (exameUtilizado * (exameUtilizadoWeight ?? 0))) / 100)

    return Number(Math.round(finalGrade).toFixed(1));
  };

  const handleOnClickPrint = async (event: any) => {
    event.preventDefault();
    const { headers, rows } = tableData;

    const idColumnIndex = headers.indexOf("Id");
    const haveBillsColumnIndex = headers.indexOf("HaveBills");

    const filteredHeaders = headers.filter((_, index) => index !== idColumnIndex);
    const filteredRows = rows.map(row =>
      row.filter((_, index) => index !== idColumnIndex)
    );

    const filteredTableData = {
      headers: filteredHeaders,
      rows: filteredRows
    };

    const studentGrades: StudentGrade[] = filteredTableData.rows.map(row => {
      const entry: Record<string, string> = {};

      filteredTableData.headers.forEach((header, index) => {
        let value = row[index];
        const hasDebt = stringToBoolean(row[0]);
        if (hasDebt && header !== "Nome" && header !== "Ordem") {
          entry[header] = "Regularizar a situação financeira";
        } else {
          entry[header] = value;
        }
      });
      return omit(entry, ["HaveBills"]) as StudentGrade;
    });
    const currentDate = new Date().toLocaleDateString("pt-PT");

    const documentTemplate: DocumentTemplateType = {
      Type: "PAUTA_EXAME",
      IsDynamicList: true,
      Headers: filteredTableData.headers.filter((_, index) => index !== haveBillsColumnIndex),
      StudentGrades: studentGrades,
      Data: {
        "cabecalho": `${classe?.university ?? ''} ${classe?.departament ?? ''} ${classe?.course ?? ''} ${classe?.level ?? ''}º Ano ${classe?.semester ?? ''}º Semestre ${currentAcademicYear?.year ?? ''} ${RegimeOptions[classe?.regime ?? 1] ?? ""}`,
        "nome_completo": userData.Fullname,
        "cargo": userData.Role.at(0) ?? "",
        "data": `${currentDate}`,
      },
      logoBase64: logoBase64 ?? '',
    }
    await exportDocument(documentTemplate, 'Pauta_Exame_');

    return;
    // event.preventDefault();

    // const { headers, rows } = tableData;

    // const idColumnIndex = headers.indexOf("Id");

    // const filteredHeaders = headers.filter((_, index) => index !== idColumnIndex);
    // const filteredRows = rows.map(row =>
    //   row.filter((_, index) => index !== idColumnIndex)
    // );

    // const filteredTableData = {
    //   headers: filteredHeaders,
    //   rows: filteredRows
    // };

    // dispatch(setPreviousPath(routes.addStudentGrades));
    // dispatch(setPdfPreviewType('STUDENTS_GRADES'));

    // dispatch(setStudentGradeData({
    //   tableData: filteredTableData,
    //   filters: {
    //     AcademicYear: currentAcademicYear?.year,
    //     Regime: RegimeOptions[classe?.regime ?? 1] ?? "",
    //     Course: classe?.course,
    //     Departament: classe?.departament,
    //     Subject: subject?.description ?? "",
    //     Level: classe?.level ?? 0,
    //     Semester: classe?.semester ?? 0,
    //     title: 'Pauta de Exame',
    //     University: classe?.university,
    //   }
    // }));

    // navigate(routes.pdfPreview);
  };

  const handleOnClickpublishResult = async (event: any) => {

    try {
      const resultData = tableData.rows.map((row: any) => {
        const rawGrade = row.at(-2);
        const grade = rawGrade === '--' ? 0 : parseFloat(rawGrade);
        return {
          idStudent: row[1],
          idSubject: subject?.id,
          idAcademicYear: currentAcademicYear?.id,
          grade: grade,
          result: row.at(-1),
          level: classe?.level,
          semester: classe?.semester,
          idCourse: classe?.idCourse
        };
      }) as StudentSubjectGradeType[];

      await Promise.all(resultData.map(grade => mutationCreateStudentSubjectGrade.mutateAsync(grade)))

      toast.success("Resultados publicados com sucesso!");
    } catch (error) {
      console.error("Error publishing results:", error);
      toast.error("Erro ao publicar resultados. Por favor, tente novamente.");
    }
  };

  useEffect(() => {
    if (tableRef.current) {
      const headers = Array.from(tableRef.current.querySelectorAll("thead th")).map((th) =>
        th.textContent?.trim() || ""
      );

      const rows = Array.from(tableRef.current.querySelectorAll("tbody tr")).map((tr) =>
        Array.from(tr.querySelectorAll("td")).map((td) => {
          const input = td.querySelector("input");
          return input ? input.value.trim() : td.textContent?.trim() || "";
        })
      );

      setTableData({ headers, rows });
      console.log("Table Data:", { headers, rows });
    }
  }, [students, assessments, watchAllAssessments]);

  const exams = assessments.filter(a => a.type!.includes('Exame'));
  return (
    <Card>
      <Flex gap="4" direction="column">
        <Flex direction='row' gap='4' style={{ alignSelf: 'end' }}>
          <Button type="button" onClick={handleOnClickPrint} variant='surface'>Extrair</Button>

          {canPublishResults && (
            <Button type="button" onClick={handleOnClickpublishResult} variant='classic'>
              Publicar resultados
            </Button>
          )}

        </Flex>
        <Table.Root ref={tableRef}>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell hidden>HaveBills</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell hidden>Id</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Ordem</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Nome</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Nota de frequência</Table.ColumnHeaderCell>
              {exams.map((assessment) => (
                <Table.ColumnHeaderCell key={assessment.id}>{assessment.type}</Table.ColumnHeaderCell>
              ))}
              <Table.ColumnHeaderCell>Nota Final</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Resultado</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {students
              .map((student, i) => {
                const studentAssessments = watchAllAssessments?.[i]?.assessments || [];
                const frequencyGrade = getFrequencyGrade(studentAssessments);
                return {
                  student,
                  studentAssessments,
                  frequencyGrade,
                  finalGrade: calculateFinalGradeV2(studentAssessments),
                  result: determineResult(frequencyGrade, studentAssessments),
                };
              })
              .filter(({ frequencyGrade }) => frequencyGrade >= IES.MINIMUM_PASSING_GRADE && frequencyGrade < 13.5)
              .map(({ student, studentAssessments, frequencyGrade, finalGrade, result }, index) => (
                <Table.Row key={student.idStudent}>
                  <Table.Cell hidden>
                    <Text weight="bold">{String(student.haveBills)}</Text>
                  </Table.Cell>
                  <Table.Cell hidden>
                    <Text weight="bold">{student.idStudent}</Text>
                  </Table.Cell>
                  <Table.Cell><Text weight="bold">{index + 1}</Text></Table.Cell>
                  <Table.Cell><Text weight="bold">{student.student}</Text></Table.Cell>
                  <Table.Cell>
                    <Text weight="bold">{Number(frequencyGrade).toFixed(1)}</Text>
                  </Table.Cell>
                  {exams.map((assessment) => (
                    <Table.Cell key={`${assessment.id}_${student.idStudent}`}>
                      {studentAssessments.find((sa: any) => sa.idAssessment === assessment.id)?.grade}
                    </Table.Cell>
                  ))}
                  <Table.Cell>
                    <Text weight="bold">
                      {result === "Reprovado" || result === "Recorrência" ? "--" : Number(finalGrade).toFixed(1)}
                    </Text>
                  </Table.Cell>
                  <Table.Cell><Text weight="bold">{result}</Text></Table.Cell>
                </Table.Row>
              ))}
          </Table.Body>

        </Table.Root>
      </Flex>
    </Card>
  );
};